import React from 'react';
import PropTypes from 'prop-types';

import styles from './lineItem.scss';

// Renders e.g. 120 -> "$120", -10 -> "-$10", 70.5 -> "$70.50" (whole amounts drop the .00).
const formatMoney = (amount, currency) => {
    if (!Number.isFinite(amount)) {
        return '';
    }
    const sign = amount < 0 ? '-' : '';
    const value = Math.abs(amount).toFixed(2).replace(/\.00$/, '');
    return `${sign}${currency}${value}`;
};

const LineItem = ({ invoiceItems }) => {
    // Hide zero-value rows (e.g. a balance-credit line when the user has no credit) so the
    // breakdown only shows amounts that affect the total.
    const lineItems = (invoiceItems?.lineItems || []).filter(item => item?.amount !== 0);

    if (lineItems.length === 0) {
        return null;
    }

    const showTotal = lineItems.length > 1 && invoiceItems?.total;

    return (
        <div className={styles.breakdown} data-testid="checkout.power-pricing.line-items">
            {lineItems.map((item, i) => {
                const itemTestId = `checkout.power-pricing.item-${item?.id ?? i}`;
                return (
                    <div className={styles.breakdownRow} key={`lineItem-${item?.id ?? i}`} data-testid={itemTestId}>
                        <span className={styles.breakdownLabel} data-testid={`${itemTestId}.description`}>
                            {item?.description}
                        </span>
                        <span className={styles.breakdownAmount} data-testid={`${itemTestId}.amount`}>
                            {formatMoney(item?.amount, item?.currency)}
                            {lineItems.length === 1 && ' total'}
                        </span>
                    </div>
                );
            })}

            {showTotal && (
                <>
                    <hr className={styles.breakdownDivider} />
                    <div className={styles.breakdownRow} data-testid="checkout.power-pricing.final-amount">
                        <span className={styles.breakdownTotalLabel} data-testid="checkout.power-pricing.final-amount.label">Total:</span>
                        <span className={styles.breakdownTotalAmount} data-testid="checkout.power-pricing.final-amount.total">
                            {formatMoney(invoiceItems.total.amount, invoiceItems.total.currency)}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

LineItem.propTypes = {
    invoiceItems: PropTypes.shape({
        lineItems: PropTypes.arrayOf(PropTypes.shape({
            description: PropTypes.string.isRequired,
            amount: PropTypes.number.isRequired,
            currency: PropTypes.string.isRequired,
        })).isRequired,
        total: PropTypes.shape({
            amount: PropTypes.number.isRequired,
            currency: PropTypes.string.isRequired,
        }),
    }),
};

LineItem.defaultProps = {
    invoiceItems: null,
};

export default LineItem;
